import axiosInstance from "core/api/axiosInstance";
import { bikeDateTimeForApi } from "../model/bikeDateTime";

const BASE_PATH = "/api/isrr";

const getToken = () => {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    null
  );
};

const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildResponse = (success, data = null, error = null, message = "") => ({
  success,
  data,
  error,
  message,
});

const extractError = (error) => {
  if (error?.response?.status === 401) return "Sesión expirada. Inicie sesión nuevamente.";
  if (error?.response?.status === 403) return "No tiene permisos para esta acción.";
  if (error?.response?.status === 404) return "Recurso no encontrado.";
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.response?.data?.msm) return error.response.data.msm;
  if (error?.message) return error.message;
  return "Error desconocido al procesar la solicitud";
};

const inflightRequests = new Map();
const responseCache = new Map();
const CACHE_TTL_MS = 3000;

const cachedFetch = async (key, fetcher) => {
  const cached = responseCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.value;
  }

  if (inflightRequests.has(key)) {
    return inflightRequests.get(key);
  }

  const promise = fetcher()
    .then((value) => {
      responseCache.set(key, { value, timestamp: Date.now() });
      return value;
    })
    .finally(() => {
      inflightRequests.delete(key);
    });

  inflightRequests.set(key, promise);
  return promise;
};

export const invalidateCache = (key) => {
  if (key) {
    responseCache.delete(key);
    inflightRequests.delete(key);
  } else {
    responseCache.clear();
    inflightRequests.clear();
  }
};

// ============================================================
// USUARIOS
// ============================================================
export const getUsersService = async () => {
  return cachedFetch("get-users", async () => {
    try {
      const { data } = await axiosInstance.get(`${BASE_PATH}/get-users`, {
        headers: authHeaders(),
      });
      const usersArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      return buildResponse(true, usersArray, null, data?.msm || "Usuarios obtenidos");
    } catch (error) {
      console.error("❌ Error getUsersService:", error);
      return buildResponse(false, [], extractError(error));
    }
  });
};

export const createUserService = async (payload) => {
  try {
    const { data } = await axiosInstance.post(`${BASE_PATH}/create-user`, payload, {
      headers: authHeaders(),
    });
    invalidateCache("get-users");
    return buildResponse(true, data?.data || data, null, data?.msm || "Usuario creado");
  } catch (error) {
    console.error("❌ Error createUserService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

export const updateUserService = async (payload) => {
  try {
    const { data } = await axiosInstance.post(`${BASE_PATH}/update-user`, payload, {
      headers: authHeaders(),
    });
    invalidateCache("get-users");
    return buildResponse(true, data?.data || data, null, data?.msm || "Usuario actualizado");
  } catch (error) {
    console.error("❌ Error updateUserService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

export const deleteUserService = async (id) => {
  try {
    const { data } = await axiosInstance.delete(`${BASE_PATH}/delete-user/${id}`, {
      headers: authHeaders(),
      data: { id },
    });
    invalidateCache("get-users");
    return buildResponse(true, data?.data || data, null, data?.msm || "Usuario eliminado");
  } catch (error) {
    console.error("❌ Error deleteUserService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

export const findUserByIdentification = (users, identification) => {
  if (!Array.isArray(users) || !identification) return null;
  return users.find((u) => String(u.identification) === String(identification)) || null;
};

// ============================================================
// BICICLETAS
// ============================================================
export const getBikesService = async () => {
  return cachedFetch("get-bikes", async () => {
    try {
      const { data } = await axiosInstance.get(`${BASE_PATH}/get-bikes`, {
        headers: authHeaders(),
      });
      const bikesArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      return buildResponse(true, bikesArray, null, data?.msm || "Bicicletas obtenidas");
    } catch (error) {
      console.error("❌ Error getBikesService:", error);
      return buildResponse(false, [], extractError(error));
    }
  });
};

export const getBikesByUser = (bikes, userId) => {
  if (!Array.isArray(bikes) || !userId) return [];
  return bikes.filter((b) => String(b.id_user) === String(userId));
};

export const createBikeService = async (payload) => {
  try {
    const cleanPayload = {
      code: payload.code,
      color: payload.color,
      id_user: payload.id_user,
    };

    if (payload.brand) cleanPayload.brand = payload.brand;
    if (payload.marca) cleanPayload.brand = payload.marca;

    const { data } = await axiosInstance.post(`${BASE_PATH}/create-bike`, cleanPayload, {
      headers: authHeaders(),
    });
    invalidateCache("get-bikes");
    return buildResponse(true, data?.data || data, null, data?.msm || "Bicicleta creada");
  } catch (error) {
    console.error("❌ Error createBikeService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

export const updateBikeService = async (payload) => {
  try {
    const { data } = await axiosInstance.post(`${BASE_PATH}/update-bike`, payload, {
      headers: authHeaders(),
    });
    invalidateCache("get-bikes");
    return buildResponse(true, data?.data || data, null, data?.msm || "Bicicleta actualizada");
  } catch (error) {
    console.error("❌ Error updateBikeService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

export const deleteBikeService = async (id) => {
  try {
    const { data } = await axiosInstance.delete(`${BASE_PATH}/delete-bike/${id}`, {
      headers: authHeaders(),
      data: { id },
    });
    invalidateCache("get-bikes");
    return buildResponse(true, data?.data || data, null, data?.msm || "Bicicleta eliminada");
  } catch (error) {
    console.error("❌ Error deleteBikeService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

// ============================================================
// CICLOS
// ============================================================
export const getDataPrkgService = async () => {
  return cachedFetch("get-data-prkg", async () => {
    try {
      const { data } = await axiosInstance.get(`${BASE_PATH}/get-data-prkg`, {
        headers: authHeaders(),
      });
      const prkgArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      return buildResponse(true, prkgArray, null, data?.msm || "Datos de parqueo obtenidos");
    } catch (error) {
      console.error("❌ Error getDataPrkgService:", error);
      return buildResponse(false, [], extractError(error));
    }
  });
};

export const createCycleService = async (payload) => {
  try {
    const { data } = await axiosInstance.post(`${BASE_PATH}/create-cycle`, payload, {
      headers: authHeaders(),
    });
    invalidateCache("get-data-prkg");
    invalidateCache("get-history");
    return buildResponse(true, data?.data || data, null, data?.msm || "Ingreso registrado");
  } catch (error) {
    console.error("❌ Error createCycleService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

export const closeCycleService = async (payload) => {
  try {
    const { data } = await axiosInstance.post(`${BASE_PATH}/close-cycle`, payload, {
      headers: authHeaders(),
    });
    invalidateCache("get-data-prkg");
    invalidateCache("get-history");
    return buildResponse(true, data?.data || data, null, data?.msm || "Salida registrada");
  } catch (error) {
    console.error("❌ Error closeCycleService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

export const getHistoryService = async (params = {}) => {
  const cacheKey = `get-history:${JSON.stringify(params)}`;
  return cachedFetch(cacheKey, async () => {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append("startDate", params.startDate);
      if (params.endDate) queryParams.append("endDate", params.endDate);

      const url = `${BASE_PATH}/get-history${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const { data } = await axiosInstance.get(url, { headers: authHeaders() });
      const eventsArray = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

      return buildResponse(true, eventsArray, null, data?.msm || "Historial obtenido");
    } catch (error) {
      console.error("❌ Error getHistoryService:", error);
      return buildResponse(false, [], extractError(error));
    }
  });
};

// ============================================================
// CONSULTAR CICLO POR QR
// ============================================================
export const getCycleByQrService = async (qrCode) => {
  try {
    const cleanCode = String(qrCode || "").trim();
    if (!cleanCode) {
      return buildResponse(false, null, "Código QR vacío");
    }

    const { data } = await axiosInstance.get(
      `${BASE_PATH}/get-cycle-by-qr/${encodeURIComponent(cleanCode)}`,
      { headers: authHeaders() }
    );

    const raw = data?.data || data;

    if (!raw) {
      return buildResponse(false, null, "No se encontró información para este QR");
    }

    const normalized = {
      id: raw.id ?? raw.id_cycle ?? null,

      user: {
        id: raw.user?.id ?? raw.id_user ?? null,
        identification: raw.user?.identification ?? raw.identification ?? "",
        names: raw.user?.names ?? raw.names ?? "",
        lastnames: raw.user?.lastnames ?? raw.lastnames ?? "",
        phone: raw.user?.phone ?? raw.user?.more ?? raw.phone ?? "",
      },

      bike: {
        id: raw.bike?.id ?? raw.id_byke ?? null,
        code: raw.bike?.code ?? raw.code ?? "",
        color: raw.bike?.color ?? raw.color ?? "",
        brand: raw.bike?.brand ?? raw.brand ?? "",
      },

      harness: raw.harness ?? raw.id_hook ?? raw.gancho ?? null,
      enter_at: raw.enter_at ?? raw.enterAt ?? null,
      out_at: raw.out_at ?? raw.outAt ?? null,
      paid: Boolean(raw.paid ?? raw.is_paid ?? raw.isPaid ?? false),
      amount: raw.amount ?? raw.total ?? 0,
      status: raw.status ?? 1,
      createdAt: raw.createdAt ?? null,
    };

    return buildResponse(true, normalized, null, data?.msm || "Ciclo encontrado");
  } catch (error) {
    console.error("❌ Error getCycleByQrService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

// ============================================================
// SUBIR IMÁGENES DEL CICLO
// ============================================================
export const uploadBikeImagesService = async (idPrkg, fotos = {}) => {
  try {
    if (!idPrkg) {
      return buildResponse(false, null, "ID de parqueo requerido");
    }

    const hasAny = Boolean(fotos.cedula) || Boolean(fotos.bicicleta) || Boolean(fotos.usuario);

    if (!hasAny) {
      return buildResponse(true, null, null, "No hay imágenes para subir");
    }

    const formData = new FormData();
    formData.append("id_prkg", String(idPrkg));

    const appendImage = async (base64, filename) => {
      if (!base64 || typeof base64 !== "string") return;

      try {
        if (base64.startsWith("data:")) {
          const res = await fetch(base64);
          const blob = await res.blob();
          formData.append("files", blob, filename);
        } else {
          const res = await fetch(`data:image/jpeg;base64,${base64}`);
          const blob = await res.blob();
          formData.append("files", blob, filename);
        }
      } catch (err) {
        console.warn(`⚠️ No se pudo procesar ${filename}:`, err);
      }
    };

    await appendImage(fotos.cedula, `cedula-${idPrkg}.jpg`);
    await appendImage(fotos.bicicleta, `bicicleta-${idPrkg}.jpg`);
    await appendImage(fotos.usuario, `usuario-${idPrkg}.jpg`);

    const token = getToken();

    const { data } = await axiosInstance.post(`${BASE_PATH}/bike-img-iserra`, formData, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "multipart/form-data",
      },
    });

    return buildResponse(true, data?.data || data, null, data?.msm || "Imágenes subidas");
  } catch (error) {
    console.error("❌ Error uploadBikeImagesService:", error);
    return buildResponse(false, null, extractError(error));
  }
};

// ============================================================
// HELPERS
// ============================================================
export const normalizeHistoryItem = (item) => {
  const inner = item?.data || {};

  return {
    id: item?.id ?? inner?.id ?? null,
    id_trans: item?.id_trans ?? null,
    id_user: inner?.id_user ?? item?.id_user ?? null,
    id_byke: inner?.id_byke ?? item?.id_byke ?? null,
    enter_at: inner?.enter_at ?? null,
    out_at: inner?.out_at ?? null,
    status: inner?.status ?? null,
    harness: inner?.harness ?? null,
    createdAt: item?.createdAt ?? inner?.createdAt ?? null,
    updatedAt: item?.updatedAt ?? inner?.updatedAt ?? null,
  };
};

export const enrichHistory = (historyRaw, users = [], bikes = []) => {
  if (!Array.isArray(historyRaw)) return [];

  const safeUsers = Array.isArray(users) ? users : [];
  const safeBikes = Array.isArray(bikes) ? bikes : [];

  const usersMap = new Map(safeUsers.map((u) => [String(u.id), u]));
  const bikesMap = new Map(safeBikes.map((b) => [String(b.id), b]));

  return historyRaw
    .filter((raw) => raw && typeof raw === "object")
    .map((raw) => {
      const inner = raw?.data || {};

      const idUser = inner?.id_user ?? raw?.id_user ?? null;
      const idByke = inner?.id_byke ?? raw?.id_byke ?? null;

      const user = usersMap.get(String(idUser)) || null;
      const bike = bikesMap.get(String(idByke)) || null;

      const nombreCompleto = user ? `${user.names || ""} ${user.lastnames || ""}`.trim() : "";
      const cedula = user?.identification || "";
      const celular = user?.more || user?.phone || "";

      const code = bike?.code || bike?.serie || "";
      const color = bike?.color || "";
      const marca = bike?.brand || bike?.marca || "";

      return {
        id: raw?.id ?? inner?.id ?? null,
        id_user: idUser,
        id_byke: idByke,
        id_trans: raw?.id_trans ?? null,

        cedula,
        nombre: nombreCompleto,
        celular,

        marca,
        color,
        serie: code,

        ganchoId: inner?.harness ?? null,
        entrada: inner?.enter_at ?? null,
        salida: inner?.out_at ?? null,
        status: inner?.status ?? null,

        createdAt: raw?.createdAt ?? inner?.createdAt ?? null,
        updatedAt: raw?.updatedAt ?? inner?.updatedAt ?? null,
      };
    });
};

// ✅ mapPrkgToGanchos: cruza con users y bikes para mostrar datos reales del usuario
export const mapPrkgToGanchos = (prkgData, totalGanchos = 30, users = [], bikes = []) => {
  const ganchos = Array.from({ length: totalGanchos }, (_, i) => ({
    id: i + 1,
    disponible: true,
    usuario: null,
    cicloId: null,
  }));

  if (!Array.isArray(prkgData)) return ganchos;

  const usersMap = new Map((users || []).map((u) => [String(u.id), u]));
  const bikesMap = new Map((bikes || []).map((b) => [String(b.id), b]));

  prkgData.forEach((item) => {
    // Solo procesar ciclos abiertos
    if (!item || item.out_at) return;

    const ganchoId = item.harness ?? item.id_hook ?? item.gancho ?? item.hook;
    if (!ganchoId) return;

    const ganchoIndex = ganchos.findIndex((g) => g.id === Number(ganchoId));
    if (ganchoIndex === -1) return;

    const user = usersMap.get(String(item.id_user)) || null;
    const bike = bikesMap.get(String(item.id_byke)) || null;

    ganchos[ganchoIndex] = {
      id: Number(ganchoId),
      disponible: false,
      cicloId: item.id,
      usuario: {
        id_user: item.id_user,
        nombre: user ? `${user.names || ""} ${user.lastnames || ""}`.trim() : "",
        cedula: user?.identification || "",
        celular: user?.more || user?.phone || "",
        color: bike?.color || "",
        marca: bike?.brand || bike?.marca || "",
        serie: bike?.code || bike?.serie || "",
        entrada: item.enter_at || null,
      },
    };
  });

  return ganchos;
};

export const buildCreateCyclePayload = (formData, userId, bikeId, harness) => ({
  id_user: userId,
  id_byke: bikeId,
  harness: harness || null,
  enter_at: formData.enter_at || bikeDateTimeForApi(),
});
